<template>
  <div class="client-hub-config">
    <v-container class="pa-0">
      <v-row dense>
        <v-col cols="12" class="d-flex align-center justify-space-between">
          <div class="client-hub-config-title">Hub Configuration</div>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" class="d-flex justify-end align-center">
          <div class="d-flex gap-2">
            <client-hub-tenant :client-sid="clientSid" @tenants-uploaded="onTenantsUploaded" />
            <cs-button
              id="client-hub-config-btnAddHubConfig"
              primary
              label="Assign Hub to Client"
              @click="openAddDialog"
            />
          </div>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="hubConfigItems"
        :header-props="{ class: 'font-weight-bold' }"
        class="elevation-2 hub-config-table"
        density="compact"
        :items-per-page="10"
        :loading="pageLoading"
        color="primary"
      >
        <template #[`item.actions`]="{ item }">
          <v-icon size="small" class="mr-2" @click="openEditDialog(item)">mdi-pencil</v-icon>
          <v-icon size="small" color="error" @click="openDeleteDialog(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-container>

    <v-snackbar v-model="uploadSnackbar.visible" :timeout="5000" location="bottom right">
      {{ uploadSnackbar.message }}
    </v-snackbar>

    <!-- Add/Edit Dialog -->
    <client-hub-config-edit-dialog
      v-model="formDialogDisplayed"
      :is-editing="isEditing"
      :hub-list="hubList"
      :hub-data="editingHubData"
      :client-sid="clientSid"
      @save="onSaveHub"
    />

    <!-- Delete Confirmation Dialog -->
    <cs-form-dialog
      id="dlg-hub-config-delete"
      v-model="deleteDialogDisplayed"
      body-id="dlg-hub-config-delete-body"
      heading="Delete Hub"
      :primary-action="{
        label: 'Delete',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onDeleteHub"
      @secondary1-click="onDeleteHubCancel"
    >
      <template #cs-form-dialog-content>
        Are you sure you want to delete hub <strong>{{ deleteTarget.displayName }}</strong
        >?
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import ClientHubTenant from './ClientHubTenant';
import ClientHubConfigEditDialog from './ClientHubConfigEditDialog';

export default {
  name: 'ClientHubConfig',

  components: {
    ClientHubTenant,
    ClientHubConfigEditDialog
  },

  extends: CSBase,

  props: {
    clientSid: { type: String, required: false, default: '' }
  },

  data() {
    return {
      hubConfig: {},
      editingHubData: {},
      headers: [
        { title: 'Key', key: 'key', sortable: true },
        { title: 'Display Name', key: 'displayName', sortable: true },
        { title: 'Organisation ID', key: 'organisationId', sortable: false },
        { title: 'User Pool ID', key: 'userPoolId', sortable: false },
        { title: 'Cognito Domain', key: 'cognitoDomain', sortable: false },
        { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
      ],
      formDialogDisplayed: false,
      deleteDialogDisplayed: false,
      isEditing: false,
      deleteTarget: { key: '', displayName: '' },
      pageLoading: false,
      uploadSnackbar: {
        visible: false,
        message: ''
      }
    };
  },

  computed: {
    hubList() {
      const tenants = this.$store.getters['hub-integration/hubTenants'] || [];
      // filter out existing hub tenants
      return tenants.filter(
        (displayName) => !this.hubConfigItems.some((item) => item.displayName === displayName)
      );
    },
    hubConfigItems() {
      return Object.entries(this.hubConfig).map(([key, value]) => ({
        key,
        displayName: value.displayName,
        organisationId: value.organisationId,
        userPoolId: value.userPoolId,
        cognitoDomain: value.cognitoDomain
      }));
    }
  },

  async mounted() {
    await this.fetchHubConfig();
    await this.fetchHubTenantList();
  },

  methods: {
    async fetchHubTenantList() {
      try {
        this.pageLoading = true;
        await this.$store.dispatch('hub-integration/getHubTenantsList');
      } catch (e) {
        console.error('fetchHubTenantList error', e);
      } finally {
        this.pageLoading = false;
      }
    },
    async fetchHubConfig() {
      if (!this.clientSid) return;
      try {
        this.pageLoading = true;
        this.setLoading(true);
        const response = await this.$store.dispatch('hub-integration/getHubConfig', {
          clientSid: this.clientSid
        });
        if (response) {
          this.hubConfig = response;
        }
      } catch (e) {
        this.clearLoading();
      } finally {
        this.pageLoading = false;
        this.clearLoading();
      }
    },
    openAddDialog() {
      this.isEditing = false;
      this.editingHubData = {};
      this.formDialogDisplayed = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      const key = item.key;
      const targetConfig = this.hubConfig[key];
      this.editingHubData = {
        key: item.key,
        ...targetConfig
      };
      this.formDialogDisplayed = true;
    },

    openDeleteDialog(item) {
      this.deleteTarget = { key: item.key, displayName: item.displayName };
      this.deleteDialogDisplayed = true;
    },
    onTenantsUploaded({ created, skipped }) {
      const uploadedText = `${created} Hub tenant${created === 1 ? '' : 's'} uploaded`;
      const skippedText = skipped > 0 ? `, ${skipped} already existed` : '';
      // Close-then-reopen so back-to-back saves re-trigger the Vuetify snackbar animation.
      this.uploadSnackbar.visible = false;
      this.$nextTick(() => {
        this.uploadSnackbar.message = `${uploadedText}${skippedText}`;
        this.uploadSnackbar.visible = true;
      });
    },

    async onSaveHub(payload) {
      try {
        this.pageLoading = true;
        this.setLoading(true);
        await this.$store.dispatch('hub-integration/postClientHubConfig', {
          clientSid: this.clientSid,
          hubConfig: payload
        });
        await this.fetchHubConfig();
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.pageLoading = false;
        this.clearLoading();
      }
    },
    handleSaveError(error) {
      // handled by external error dialog
      console.error('handleSaveError', error);
    },
    onDeleteHubCancel() {
      this.deleteDialogDisplayed = false;
      this.deleteTarget = { key: '', displayName: '' };
    },

    async onDeleteHub() {
      const targetConfig = this.hubConfig[this.deleteTarget.key];
      try {
        this.pageLoading = true;
        this.setLoading(true);
        await this.$store.dispatch('hub-integration/deleteClientHubConfig', {
          clientSid: this.clientSid,
          hubConfig: targetConfig
        });
        await this.fetchHubConfig();
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.pageLoading = false;
        this.clearLoading();
      }
      this.deleteDialogDisplayed = false;
    }
  }
};
</script>

<style scoped>
.client-hub-config-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
}

.hub-config-table {
  margin-top: 10px;
}
</style>
